import { useState, useEffect } from 'react';
import {
  Alert, Box, Button, Card, CardContent, CardHeader, Chip,
  Grid, LinearProgress, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import TimelineIcon from '@mui/icons-material/Timeline';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import ListAltIcon from '@mui/icons-material/ListAlt';
import PieChartIcon from '@mui/icons-material/PieChart';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import StatusBadge, { PIPELINE_STATUS_MAP } from '../../components/shared/StatusBadge';
import SectionHeader from '../../components/shared/SectionHeader';
import PageHeader from '../../components/shared/PageHeader';
import StatCard from '../../components/shared/StatCard';
import LoadingSkeleton from '../../components/shared/LoadingSkeleton';
import TrendChart from '../../components/shared/TrendChart';

const LenderDashboardPage = () => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setTimeout(() => {
      const today = new Date();
      const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
      const daysLeft = daysInMonth - today.getDate();

      setMetrics({
        wac: 6.847,
        weightedAvgDTI: 38.2,
        avgLoanAmount: 487500,
        avgFICO: 742,
        submissionsMTD: 147,
        locksMTD: 98,
        fundingsMTD: 72,
        submissionsToday: 8,
        locksToday: 5,
        fundingsToday: 3,
        pullThroughRatio: 66.7,
        pipelineVolume: 142500000,
        activeBrokers: 24,
        avgDaysToClose: 28,
        daysInMonth,
        daysLeft,
        currentDay: today.getDate(),
        dailySubmissions: [12, 15, 9, 14, 11, 13, 8],
        dailyLocks: [8, 10, 6, 9, 7, 8, 5],
        dailyFundings: [5, 4, 6, 3, 5, 4, 3],
        topBrokers: [
          { name: 'Prime Mortgage Group', volume: 28500000, loans: 42 },
          { name: 'Elite Lending Partners', volume: 22300000, loans: 35 },
          { name: 'First Choice Brokers', volume: 18700000, loans: 28 },
        ],
        statusBreakdown: { submitted: 45, inReview: 32, approved: 28, locked: 98, funded: 72 },
        productMix: { conventional: 62, fha: 18, va: 12, jumbo: 8 },
        loanPurpose: { purchase: 68, refinance: 32 },
      });
      setLoading(false);
    }, 500);
  }, []);

  if (loading) {
    return <LoadingSkeleton variant="page" cards={4} />;
  }

  const m = metrics;

  const MtdCard = ({ title, value, today, pct, target, color }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <SectionHeader title={title} />
        <Typography variant="h3" fontWeight={700} color={color} className="tabular-nums" sx={{ mb: 1 }}>{value}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Today: <Typography component="span" fontWeight={700} color={color}>{today}</Typography>
        </Typography>
        <LinearProgress variant="determinate" value={pct} color="primary" sx={{ height: 6, borderRadius: 1, mb: 0.5 }} />
        <Typography variant="caption" color="text.secondary">{pct}% of monthly target ({target})</Typography>
      </CardContent>
    </Card>
  );

  const ProgressRow = ({ label, value, color }) => (
    <Box sx={{ mb: 2 }}>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography variant="body2">{label}</Typography>
        <Typography variant="body2" fontWeight={700}>{value}%</Typography>
      </Stack>
      <LinearProgress variant="determinate" value={value} sx={{ height: 6, borderRadius: 1, '& .MuiLinearProgress-bar': { bgcolor: color } }} />
    </Box>
  );

  return (
    <Box>
      <PageHeader
        title="Lender Dashboard"
        subtitle="Real-time portfolio metrics and performance indicators"
        actions={<Button variant="contained" startIcon={<DownloadIcon />}>Export Report</Button>}
      />

      {/* Calendar */}
      <Alert severity="info" icon={<CalendarMonthIcon />} sx={{ mb: 3 }}>
        <strong>Month Progress:</strong> Day {m.currentDay} of {m.daysInMonth} •{' '}
        <strong>{m.daysLeft} days remaining</strong> •{' '}
        {Math.round((m.currentDay / m.daysInMonth) * 100)}% complete
      </Alert>

      {/* Portfolio Metrics */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, lg: 3 }}><StatCard label="Weighted Avg Coupon (WAC)" value={`${m.wac.toFixed(3)}%`} caption="Portfolio average rate" icon={<TrendingDownIcon color="success" />} /></Grid>
        <Grid size={{ xs: 6, lg: 3 }}><StatCard label="Weighted Avg DTI" value={`${m.weightedAvgDTI.toFixed(1)}%`} caption="Portfolio average DTI" icon={<TrendingDownIcon color="success" />} /></Grid>
        <Grid size={{ xs: 6, lg: 3 }}><StatCard label="Avg Loan Amount" value={`$${(m.avgLoanAmount / 1000).toFixed(0)}K`} caption="Average loan size" icon={<TrendingUpIcon color="info" />} /></Grid>
        <Grid size={{ xs: 6, lg: 3 }}><StatCard label="Avg FICO Score" value={m.avgFICO} caption="Portfolio average" icon={<TrendingUpIcon color="info" />} /></Grid>
      </Grid>

      {/* MTD Performance */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, lg: 4 }}><MtdCard title="Submissions MTD" value={m.submissionsMTD} today={m.submissionsToday} pct={75} target={196} color="primary.main" /></Grid>
        <Grid size={{ xs: 12, lg: 4 }}><MtdCard title="Locks MTD" value={m.locksMTD} today={m.locksToday} pct={70} target={140} color="success.main" /></Grid>
        <Grid size={{ xs: 12, lg: 4 }}><MtdCard title="Fundings MTD" value={m.fundingsMTD} today={m.fundingsToday} pct={72} target={100} color="info.main" /></Grid>
      </Grid>

      {/* 7-Day Trend + Pipeline Health */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, lg: 8 }}>
          <Card sx={{ height: '100%' }}>
            <CardHeader title={<SectionHeader title="7-Day Activity Trend" icon={<TimelineIcon fontSize="small" />} sx={{ mb: 0 }} />} />
            <CardContent sx={{ pt: 0 }}>
              <TrendChart
                data={['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, i) => ({
                  label: day,
                  submissions: m.dailySubmissions[i],
                  locks: m.dailyLocks[i],
                  fundings: m.dailyFundings[i],
                }))}
                series={[
                  { key: 'submissions', label: 'Submissions', color: 'primary' },
                  { key: 'locks', label: 'Locks', color: 'success' },
                  { key: 'fundings', label: 'Fundings', color: 'info' },
                ]}
                height={260}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, lg: 4 }}>
          <Card sx={{ height: '100%' }}>
            <CardHeader title={<SectionHeader title="Pipeline Health" icon={<MonitorHeartIcon fontSize="small" />} sx={{ mb: 0 }} />} />
            <CardContent>
              <Box sx={{ mb: 2 }}>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                  <Typography variant="body2">Pull-Through Ratio</Typography>
                  <Typography variant="body2" fontWeight={700} color="success.main">{m.pullThroughRatio.toFixed(1)}%</Typography>
                </Stack>
                <LinearProgress variant="determinate" value={m.pullThroughRatio} color="success" sx={{ height: 6, borderRadius: 1 }} />
                <Typography variant="caption" color="text.secondary">{m.fundingsMTD} funded / {m.locksMTD} locked</Typography>
              </Box>
              {[
                { label: 'Pipeline Volume', value: `$${(m.pipelineVolume / 1e6).toFixed(1)}M` },
                { label: 'Active Brokers', value: m.activeBrokers },
                { label: 'Avg Days to Close', value: `${m.avgDaysToClose} days` },
              ].map(({ label, value }) => (
                <Stack key={label} direction="row" justifyContent="space-between" sx={{ py: 0.75, borderBottom: 1, borderColor: 'divider' }}>
                  <Typography variant="body2">{label}</Typography>
                  <Typography variant="body2" fontWeight={700}>{value}</Typography>
                </Stack>
              ))}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Status Breakdown + Product Mix */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, lg: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardHeader title={<SectionHeader title="Pipeline Status" icon={<ListAltIcon fontSize="small" />} sx={{ mb: 0 }} />} />
            <CardContent>
              {[
                { status: 'Submitted', desc: 'New submissions under review', count: m.statusBreakdown.submitted },
                { status: 'In Review', desc: 'Active underwriting', count: m.statusBreakdown.inReview },
                { status: 'Approved', desc: 'Approved, pending lock', count: m.statusBreakdown.approved },
                { status: 'Locked', desc: 'Rate locked', count: m.statusBreakdown.locked },
                { status: 'Funded', desc: 'Closed loans', count: m.statusBreakdown.funded },
              ].map(({ status, desc, count }) => (
                <Stack key={status} direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <StatusBadge status={status} map={PIPELINE_STATUS_MAP} />
                    <Typography variant="body2">{desc}</Typography>
                  </Stack>
                  <Typography variant="h6" fontWeight={700}>{count}</Typography>
                </Stack>
              ))}
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardHeader title={<SectionHeader title="Product Mix & Purpose" icon={<PieChartIcon fontSize="small" />} sx={{ mb: 0 }} />} />
            <CardContent>
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Product Type</Typography>
              <ProgressRow label="Conventional" value={m.productMix.conventional} color="primary.main" />
              <ProgressRow label="FHA" value={m.productMix.fha} color="success.main" />
              <ProgressRow label="VA" value={m.productMix.va} color="info.main" />
              <ProgressRow label="Jumbo" value={m.productMix.jumbo} color="warning.main" />

              <Typography variant="subtitle2" sx={{ mt: 3, mb: 1.5 }}>Loan Purpose</Typography>
              <ProgressRow label="Purchase" value={m.loanPurpose.purchase} color="info.main" />
              <ProgressRow label="Refinance" value={m.loanPurpose.refinance} color="secondary.main" />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Top Performers */}
      <Card>
        <CardHeader title={<SectionHeader title="Top Performing Brokers MTD" icon={<EmojiEventsIcon fontSize="small" />} sx={{ mb: 0 }} />} />
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Rank</TableCell>
                <TableCell>Broker</TableCell>
                <TableCell align="right">Volume</TableCell>
                <TableCell align="right">Loans</TableCell>
                <TableCell align="right">Avg Loan Size</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {m.topBrokers.map((broker, idx) => (
                <TableRow key={idx}>
                  <TableCell>
                    <Chip
                      label={`#${idx + 1}`}
                      size="small"
                      color={idx === 0 ? 'warning' : idx === 1 ? 'default' : 'secondary'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell><Typography variant="body2" fontWeight={700}>{broker.name}</Typography></TableCell>
                  <TableCell align="right"><Typography variant="body2" fontWeight={700} color="primary.main">${(broker.volume / 1e6).toFixed(1)}M</Typography></TableCell>
                  <TableCell align="right">{broker.loans}</TableCell>
                  <TableCell align="right">${(broker.volume / broker.loans / 1000).toFixed(0)}K</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Card>
    </Box>
  );
};

export default LenderDashboardPage;
